import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiMail, FiBell, FiUser } from "react-icons/fi";
import { useUser } from "../context/UserContext";
import { permissionService } from "../services/permissionService";
import { API_BASE, api } from "../utils/api";
import {
  applyThemeToDocument,
  getStoredTheme,
  THEME_STORAGE_KEY,
} from "../utils/theme";
import {
  PLAN_STATE_UPDATED_EVENT,
  getPlanSnapshotFromPlanState,
  persistPlanSnapshot,
} from "../utils/planSync";

// ASSETS
import Searchimg from "../assets/search.png";
import Micimg from "../assets/mic.png";
import Lightimg from "../assets/light.png";
import Darkimg from "../assets/dark.png";
import setimg from "../assets/settings.png";
import nyafaz from "../assets/nyafazlogo.png";
import mailimg from "../assets/mail.png";
import bellimg from "../assets/bell.png";


const formatNotificationTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};




const Header = ({ showLogo = false }) => {
  const { user, loading } = useUser();
  const [open, setOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [planStatus, setPlanStatus] = useState(
    String(localStorage.getItem("plan_status") || "active").toLowerCase()
  );
  const [displayPlanLabel, setDisplayPlanLabel] = useState(
    permissionService.getDisplayPlanLabel?.() || "Free Plan"
  );
  const [theme, setTheme] = useState(() => getStoredTheme());
  const [scanLimitSummary, setScanLimitSummary] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const notificationRef = useRef(null);

  const showRechargeCta =
    scanLimitSummary?.canRecharge &&
    Number(scanLimitSummary?.effectiveLimit || scanLimitSummary?.scanLimit || 0) > 0 &&
    (Number(scanLimitSummary?.scansRemaining || 0) <= 0 ||
      Number(scanLimitSummary?.scansRemaining || 0) /
        Number(scanLimitSummary?.effectiveLimit || scanLimitSummary?.scanLimit || 1) <=
        0.1);

  const handleRechargeClick = () => {
    navigate("/nyafaz-salon/ib-dashboard#scan-limits");
  };

  const effectiveLimitDisplay = Number(
    scanLimitSummary?.effectiveLimit ?? scanLimitSummary?.scanLimit ?? 0
  );

  const today = new Date().toLocaleDateString("en-GB");

  const handleLogout = async () => {
    const userId = localStorage.getItem("userId");
    try {
      if (userId) {
        await api("/logout", {
          method: "POST",
          body: JSON.stringify({ userId }),
        });
      }
    } catch {
      /* ignore */
    }
    localStorage.removeItem("userId");
    localStorage.removeItem("orgId");
    localStorage.removeItem("accessModules");
    localStorage.removeItem("role");
    localStorage.removeItem("portalTenantView");
    localStorage.removeItem("portalTenantOrgId");
    localStorage.removeItem("plan");
    localStorage.removeItem("plan_status");
    navigate("/nyafaz-salon");
  };

  const applyTheme = (nextTheme) => {
    const resolvedTheme = applyThemeToDocument(nextTheme);
    setTheme(resolvedTheme);
    localStorage.setItem(THEME_STORAGE_KEY, resolvedTheme);
    window.dispatchEvent(new CustomEvent("themechange", { detail: resolvedTheme }));
  };

  const enableLightMode = () => applyTheme("light");
  const enableDarkMode = () => applyTheme("dark");

  const updateHeaderPlanLabel = (nextPlanState) => {
    const snapshot = getPlanSnapshotFromPlanState(nextPlanState);
    if (!snapshot) return;

    persistPlanSnapshot(snapshot);
    setPlanStatus(snapshot.planStatus || "active");
    setDisplayPlanLabel(snapshot.displayPlanLabel);
  };

  useEffect(() => {
    const token = localStorage.getItem("userId");
    if (!token) return;

    let timer;
    const fetchNotifications = async ({ silent = false } = {}) => {
      if (!silent) {
        setNotificationsLoading(true);
      }
      try {
        const organizationId = localStorage.getItem("orgId");
        const scanParams = new URLSearchParams();
        if (organizationId && organizationId !== "undefined" && organizationId !== "null") {
          scanParams.set("organization_id", organizationId);
        }

        const [notificationsRes, planStateRes, scanLimitRes] = await Promise.all([
          fetch(`${API_BASE}/notifications`, {
            credentials: 'include',
            headers: { },
          }),
          fetch(`${API_BASE}/subscriptions/plan-state`, {
            credentials: 'include',
            headers: { },
          }),
          fetch(`${API_BASE}/users/scan-limit-summary?${scanParams.toString()}`, {
            credentials: 'include',
            headers: { },
            cache: "no-store",
          }),
        ]);

        if (scanLimitRes.ok) {
          const scanData = await scanLimitRes.json();
          setScanLimitSummary(scanData);
        }

        const data = await notificationsRes.json();
        setNotifications(data?.notifications || []);
        setUnreadCount(Number(data?.unread_count || 0));

        if (planStateRes.ok) {
          const planStateData = await planStateRes.json();
          const nextPlanState = planStateData?.data || null;
          updateHeaderPlanLabel(nextPlanState);
        }
      } catch {
        setNotifications([]);
        setUnreadCount(0);
      } finally {
        if (!silent) {
          setNotificationsLoading(false);
        }
      }
    };

    fetchNotifications({ silent: notificationsOpen });

    if (!notificationsOpen) {
      timer = setInterval(() => {
        fetchNotifications({ silent: true });
      }, 10000);
    }

    return () => clearInterval(timer);
  }, [location.pathname, notificationsOpen]);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    const syncTheme = () => {
      const nextTheme = getStoredTheme();
      setTheme((currentTheme) => (currentTheme === nextTheme ? currentTheme : nextTheme));
      applyThemeToDocument(nextTheme);
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("themechange", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("themechange", syncTheme);
    };
  }, []);

  useEffect(() => {
    const handlePlanStateUpdate = (event) => {
      const nextSnapshot = event?.detail;
      if (nextSnapshot?.displayPlanLabel) {
        setDisplayPlanLabel(nextSnapshot.displayPlanLabel);
      }
      if (nextSnapshot?.planStatus) {
        setPlanStatus(String(nextSnapshot.planStatus).toLowerCase());
      }
    };

    window.addEventListener(PLAN_STATE_UPDATED_EVENT, handlePlanStateUpdate);
    return () => {
      window.removeEventListener(PLAN_STATE_UPDATED_EVENT, handlePlanStateUpdate);
    };
  }, []);

  // Close notification when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [notificationsOpen]);

  const markNotificationRead = async (notificationId) => {
    const token = localStorage.getItem("userId");
    if (!token) return;

    try {
      await fetch(`${API_BASE}/notifications/read`, {
            credentials: 'include',
            method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(notificationId ? { notification_id: notificationId } : {}),
      });

      setNotifications((prev) => prev.map((item) => (
        !notificationId || item.id === notificationId
          ? { ...item, is_read: true }
          : item
      )));
      if (!notificationId) {
        setUnreadCount(0);
      } else {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      // Keep existing UI state if read update fails.
    }
  };

  const notificationTone = (type) => {
    if (type === "warning") return "salon-notifications-item--warning";
    if (type === "success") return "salon-notifications-item--success";
    return "";
  };

  if (loading) {
    return <div className="p-4 text-white">Loading...</div>;
  }

  return (
    <div className="header-shell relative z-50 w-full pt-3 md:pt-3.5 lg:pt-4">
      <div className="header-shell__inner relative flex min-h-[4.25rem] w-full flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/10 px-3 py-2 backdrop-blur-xl md:min-h-[4.5rem] md:px-4 lg:h-20 lg:flex-nowrap lg:gap-4 lg:px-6 lg:py-0 lg:pr-10 before:pointer-events-none before:absolute before:inset-0 before:rounded-2xl before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_28px),radial-gradient(circle_at_bottom_left,transparent_16px,black_28px)]">
        <div className="header-shell__left flex min-w-0 flex-1 items-center gap-3 lg:w-[35%] lg:flex-none">
          {showLogo ? (
            <div className="pl-1 lg:pl-4">
              <img src={nyafaz} alt="Nyafaz Logo" className="h-8 object-contain md:h-9 lg:h-10" />
            </div>
          ) : null}
        </div>

        <div className="header-shell__actions flex w-full min-w-0 flex-wrap items-center justify-end gap-2 sm:gap-3 md:w-auto md:flex-nowrap lg:gap-6">
          {/* Light / Dark Toggle - Only on other pages */}
          {!showLogo && (
            <div className="header-shell__theme-toggle relative flex items-center overflow-hidden rounded-xl bg-white/5 shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] backdrop-blur-3xl before:pointer-events-none before:absolute before:inset-0 before:rounded-xl before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)]">
              <button
                type="button"
                onClick={enableLightMode}
                aria-pressed={theme === "light"}
                className={`relative z-10 flex items-center gap-2 px-2.5 py-1.5 text-xs font-semibold transition-all md:px-3 lg:px-4 ${
                  theme === "light"
                  ? "bg-white/20 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)]"
                  : "bg-transparent text-white/70 hover:bg-white/5"
                }`}
              >
                <img src={Lightimg} className="w-3.5 h-3.5 brightness-200" alt="" />
                <span className="header-shell__theme-label hidden lg:inline">Light</span>
              </button>
              <div className="relative z-10 h-6 w-px bg-white/10"></div>
              <button
                type="button"
                onClick={enableDarkMode}
                aria-pressed={theme === "dark"}
                className={`relative z-10 flex items-center gap-2 px-2.5 py-1.5 text-xs font-semibold transition-all md:px-3 lg:px-4 ${
                  theme === "dark"
                  ? "bg-white/20 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)]"
                  : "bg-transparent text-white/70 hover:bg-white/5"
                }`}
              >
                <span className="header-shell__theme-label hidden lg:inline">Dark</span>
                <img src={Darkimg} alt="" className="header-icon-img w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 md:gap-3 lg:gap-4">
            {/* <div className="relative w-8 h-8 rounded-full bg-white/5 backdrop-blur-3xl shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] before:content-[''] before:absolute before:inset-0 before:rounded-full before:pointer-events-none before:border-[1px] before:border-white/50 before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] flex items-center justify-center cursor-pointer hover:bg-white/10 transition">
              <img src={setimg} alt="Settings" className="w-5 h-5 brightness-0 opacity-80" />
            </div> */}
           {/* <div className="relative w-8 h-8 rounded-full bg-white/5 backdrop-blur-3xl shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] before:content-[''] before:absolute before:inset-0 before:rounded-full before:pointer-events-none before:border-[1px] before:border-white/50 before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] flex items-center justify-center cursor-pointer hover:bg-white/10 transition">
              <img src={mailimg} alt="Mail" className="w-5 h-5 brightness-0 opacity-80" />
            </div> */}
            <div className="relative" ref={notificationRef}>
              <button
                type="button"
                onClick={() => {
                  setNotificationsOpen((prev) => !prev);
                  setOpen(false);
                }}
                className="header-shell__icon-btn relative w-8 h-8 rounded-full bg-white/5 backdrop-blur-3xl shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] before:content-[''] before:absolute before:inset-0 before:rounded-full before:pointer-events-none before:border-[1px] before:border-white/50 before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] flex items-center justify-center cursor-pointer hover:bg-white/10 transition"
                title="Notifications"
              >
                <FiBell className="header-icon w-5 h-5" aria-hidden />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 border border-white/60 text-[10px] text-white font-bold flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="salon-notifications-panel">
                  <div className="salon-notifications-panel__header">
                    <div>
                      <p className="salon-notifications-panel__title">Notifications</p>
                      <p className="salon-notifications-panel__subtitle">{unreadCount} unread</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => markNotificationRead(null)}
                      className="salon-notifications-panel__mark-read disabled:opacity-50"
                      disabled={!unreadCount}
                    >
                      Mark all read
                    </button>
                  </div>

                  <div className="salon-notifications-panel__list">
                    {notificationsLoading ? (
                      <div className="salon-notifications-panel__loading px-3 py-6 text-center">Loading notifications...</div>
                    ) : notifications.length ? (
                      notifications.map((item) => (
                        <div
                          key={item.id}
                          className={`salon-notifications-item ${notificationTone(item.type)} ${item.is_read ? "is-read" : ""}`}
                        >
                          <button
                            type="button"
                            onClick={() => !item.is_read && markNotificationRead(item.id)}
                            className="w-full text-left"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="salon-notifications-item__title">{item.title}</p>
                                <p className="salon-notifications-item__message">{item.message}</p>
                              </div>
                              {!item.is_read && <span className="mt-1 w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                            </div>
                            <p className="salon-notifications-item__time">{formatNotificationTime(item.created_at)}</p>
                          </button>
                          
                        </div>
                      ))
                    ) : (
                      <div className="salon-notifications-panel__empty px-3 py-6 text-center">No notifications yet.</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="header-shell__plan-badge relative flex max-w-full flex-wrap items-center gap-2 rounded-full bg-white/10 px-2.5 py-1.5 shadow-[inset_0_1px_2px_rgba(255,255,255,0.35)] backdrop-blur-3xl md:gap-3 md:px-3 before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_12px,black_20px),radial-gradient(circle_at_bottom_left,transparent_12px,black_20px)]">
              <div className="flex items-center gap-1.5 md:gap-2">
                <span className="header-shell__scan-label text-xs text-white/80">Scans</span>
                <span className="text-sm font-semibold text-white" data-testid="header-scan-limit">
                  {scanLimitSummary
                    ? `${Number(scanLimitSummary.scansRemaining || 0).toLocaleString("en-IN")} / ${effectiveLimitDisplay.toLocaleString("en-IN")}`
                    : "—"}
                </span>
                {scanLimitSummary?.topupScans > 0 && (
                  <span className="header-scan-topup text-[10px] font-semibold">
                    +{Number(scanLimitSummary.topupScans).toLocaleString("en-IN")} top-up
                  </span>
                )}
              </div>
              {showRechargeCta && (
                <button
                  type="button"
                  onClick={handleRechargeClick}
                  className="relative z-10 rounded-full bg-[#0ad4c8]/25 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide header-scan-recharge-btn hover:bg-[#0ad4c8]/40"
                >
                  Buy scans
                </button>
              )}
              <span className="header-shell__plan-label relative z-10 whitespace-nowrap text-[10px] font-semibold text-white md:text-[11px]">
                {displayPlanLabel}
              </span>
            </div>
          </div>

          <div className="header-shell__divider hidden h-8 w-px bg-white/50 lg:mx-2 lg:block" />

          <div
            className="header-shell__profile relative flex min-w-0 cursor-pointer items-center gap-2 md:gap-3"
            onClick={() => {
              setOpen(!open);
              setNotificationsOpen(false);
            }}
          >
            <div className="relative group">
              {(user?.org_logo || user?.data?.org_logo || user?.user?.org_logo) ? (
                <div className="relative w-11 h-11 rounded-full bg-white/5 backdrop-blur-3xl shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] before:content-[''] before:absolute before:inset-0 before:rounded-full before:pointer-events-none before:border-[1px] before:border-white/50 before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] p-0.5">
                  <img
                    src={(user?.org_logo || user?.data?.org_logo || user?.user?.org_logo)?.startsWith("http") ? (user?.org_logo || user?.data?.org_logo || user?.user?.org_logo) : `${API_BASE}/${(user?.org_logo || user?.data?.org_logo || user?.user?.org_logo)?.replace(/^\//, "")}`}
                    alt="Profile"
                    className="w-full h-full rounded-full object-contain"
                  />
                </div>
              ) : (
                <div className="relative w-11 h-11 rounded-full bg-white/5 backdrop-blur-3xl shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] before:content-[''] before:absolute before:inset-0 before:rounded-full before:pointer-events-none before:border-[1px] before:border-white/50 before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] flex items-center justify-center">
                  <FiUser className="header-icon" size={22} />
                </div>
              )}
            </div>
            <div className="header-shell__user-meta hidden text-right sm:block">
              <p className="max-w-[9rem] truncate text-sm font-semibold md:max-w-[12rem]">
                {user?.fullName || user?.data?.fullName || user?.user?.fullName || user?.full_name || "Admin"}
              </p>
              <p className="header-shell__user-date text-xs text-white">{today}</p>
            </div>

            {/* DROPDOWN */}
            {open && (
              <div className="absolute right-0 top-14 w-48
                bg-[#0a6b78]/85 backdrop-blur-3xl rounded-xl text-white z-[9999] overflow-hidden
                before:content-[''] before:absolute before:inset-0 before:rounded-xl before:pointer-events-none before:border-[1px] before:border-white/50 before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in]"
              >
                <button
                  onClick={() => navigate("/nyafaz-salon/edit-profile")}
                  className="w-full text-left px-4 py-2 hover:bg-white/10 transition-colors cursor-pointer text-sm font-medium"
                >
                  Edit Profile
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 hover:bg-white/10 transition-colors cursor-pointer text-sm font-medium"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
